/**
 * Local Document State
 *
 * Stores per-document settings that should NOT sync across devices:
 * - mindmapTransform (zoom, panX, panY)
 * - bubbleTransform (zoom, panX, panY)
 * (Bubble's saved layout: interfaces/classic/store/bubble-positions.js.)
 * - currentView (dashboard, mindmap, bubble, gantt, etc.)
 * - notesPanelOpen (boolean)
 * - currentNoteId (string|null)
 * - ganttZoom (number)
 * - ganttScale (string: "auto", "day", "week", "month")
 * - hideCompleted (boolean)
 * - focusId (string: current focus node ID)
 *
 * Also stores the last opened document ID for restore on refresh.
 *
 * Note: Document content and userSetProjectDates are stored in Doc CRDT.
 * This module only handles per-device UI state.
 */

import { emit, Events } from "../engine/core/events.js";
import * as StorageHealth from "../engine/sync/storage-health.js";
import {
  getPref,
  setPref,
  removePref,
  clearPrefsByPrefix,
} from "../engine/storage/index.js";

const DOC_STATE_PREFIX = "orbital-doc-state-";
const LAST_OPENED_KEY = "orbital-last-opened";

// M5 Fix (PLAN18): Rate limit quota warnings to prevent UI spam
let lastQuotaWarning = 0;
const QUOTA_WARNING_INTERVAL = 60000; // 1 minute

/**
 * Emit quota warning with rate limiting (main.js shows it). A view setting
 * that cannot be saved does not stop editing: the maps are stored
 * elsewhere (IndexedDB); decided 2026-10-11.
 * @private
 */
function emitQuotaWarning() {
  const now = Date.now();
  if (now - lastQuotaWarning > QUOTA_WARNING_INTERVAL) {
    lastQuotaWarning = now;
    emit(Events.STORAGE_WARNING, { type: 'quota_exceeded' });
  }
}

/**
 * Get local state for a document
 * @param {string} docId
 * @returns {Object|null} { mindmapTransform, bubbleTransform, notesPanelOpen, currentView }
 */
export function getDocState(docId) {
  if (!docId) return null;
  try {
    const stored = getPref(DOC_STATE_PREFIX + docId);
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch (err) {
    console.error("[LocalDocState] Failed to load:", err);
    return null;
  }
}

/**
 * Save local state for a document
 * @param {string} docId
 * @param {Object} state - { mindmapTransform, bubbleTransform, notesPanelOpen, currentView }
 */
export function saveDocState(docId, state) {
  if (!docId) return;
  try {
    const existing = getDocState(docId) || {};
    const merged = {
      ...existing,
      ...state,
      updatedAt: Date.now(),
    };
    setPref(DOC_STATE_PREFIX + docId, JSON.stringify(merged));
  } catch (err) {
    console.error("[LocalDocState] Failed to save:", err);
    // Phase 2.9 (PLAN17): Notify user of storage quota issues
    // M5 Fix (PLAN18): Use rate-limited helper
    if (StorageHealth.isQuotaError(err)) {
      emitQuotaWarning();
    }
  }
}

/**
 * Delete local state for a document
 * @param {string} docId
 */
export function deleteDocState(docId) {
  if (!docId) return;
  removePref(DOC_STATE_PREFIX + docId);
}

/**
 * Clear all document states
 */
export function clearAllDocStates() {
  clearPrefsByPrefix(DOC_STATE_PREFIX);
}

/**
 * Get the last opened document.
 *
 * Reads sessionStorage first (per-tab) so each browser tab restores its
 * OWN doc on refresh. Falls back to localStorage (cross-tab "last open
 * anywhere") for fresh tabs that don't have session state yet.
 *
 * @returns {Object|null} { docId, timestamp }
 */
export function getLastOpenedDoc() {
  try {
    // sessionStorage is per-tab and stays direct: it's not a persistence
    // concern (cleared when the tab closes) and works the same in any
    // webview. Only the cross-tab fallback goes through the adapter.
    const sessStored = sessionStorage.getItem(LAST_OPENED_KEY);
    if (sessStored) return JSON.parse(sessStored);
    const stored = getPref(LAST_OPENED_KEY);
    if (stored) return JSON.parse(stored);
    return null;
  } catch (err) {
    console.error("[LocalDocState] Failed to get last opened:", err);
    return null;
  }
}

/**
 * Set the last opened document.
 *
 * Writes BOTH sessionStorage (per-tab) and localStorage (cross-tab).
 * Each tab's session retains its own current doc on refresh; a brand-new
 * tab opens whichever doc was last opened anywhere in the browser.
 *
 * @param {string} docId
 * @param {Object} [opts]
 * @param {boolean} [opts.sessionOnly] - update only this tab's entry.
 *   Used when a boot-time restore falls back to a doc the user didn't
 *   explicitly open here: the tab must remember it (or every reload
 *   re-runs the fallback and follows whatever doc another tab edited
 *   last), but it shouldn't become the cross-tab default for new tabs.
 */
export function setLastOpenedDoc(docId, opts = {}) {
  if (!docId) return;
  try {
    const value = JSON.stringify({
      docId,
      timestamp: Date.now(),
    });
    sessionStorage.setItem(LAST_OPENED_KEY, value);
    if (!opts.sessionOnly) setPref(LAST_OPENED_KEY, value);
  } catch (err) {
    console.error("[LocalDocState] Failed to set last opened:", err);
    // Phase 2.9 (PLAN17): Notify user of storage quota issues
    // M5 Fix (PLAN18): Use rate-limited helper
    if (StorageHealth.isQuotaError(err)) {
      emitQuotaWarning();
    }
  }
}

/**
 * Clear the last opened document
 */
export function clearLastOpenedDoc() {
  sessionStorage.removeItem(LAST_OPENED_KEY);
  removePref(LAST_OPENED_KEY);
}
